import * as R from 'ramda';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { createSelectorCreator, lruMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import config from 'config';
import { Subscription } from 'utils/apiClient/services/core/types';
import { Role } from 'CommonAPI';

dayjs.extend(utc);
// create a "selector creator" that uses lodash.isEqual instead of ===
const createDeepEqualSelector = createSelectorCreator(lruMemoize, isEqual);

const { TRIAL_SUBSCRIPTION_ID } = config;

export const getUserToken = () =>
  JSON.parse(localStorage.getItem('account') as string).userToken;
export const getIsTrialUser = (subs: Subscription[]) =>
  getActiveSubscriptionNames(subs).includes('Trial');

export const getCanUseGiftcardsV2 = () =>
  JSON.parse(localStorage.getItem('account') as string).hasGiftcardsV2Sub;

export const getActiveSubscriptions = (subs: Subscription[]) => {
  if (!subs || !Array.isArray(subs)) {
    return [];
  }

  const result = R.filter((it: Subscription) => it.active, subs);
  return result;
};

export const getActiveSubscriptionNames = createDeepEqualSelector(
  getActiveSubscriptions,
  (subs) => R.map((it: Subscription) => it.subscription_plan.name, subs)
);

export const getActivePaidSubscriptions = (subs: Subscription[]) => {
  if (!subs || !Array.isArray(subs)) return [];

  return subs.filter(
    (s) =>
      s.active &&
      !s.subscription_plan.name.includes('Trial') &&
      !s.subscription_plan.name.includes('Free')
  );
};

export const getHighestActivePaidSubscription = (
  subs: Subscription[]
): Subscription | undefined => {
  if (!subs || !Array.isArray(subs)) {
    return undefined;
  }
  const activeSubscriptions = getActivePaidSubscriptions(subs);
  if (activeSubscriptions.length === 0) {
    return undefined;
  }
  return R.compose<Subscription[], Subscription[], Subscription | undefined>(
    R.head,
    R.sort(R.descend((s) => s.subscription_plan.id))
  )(activeSubscriptions) as Subscription | undefined;
};

export const getLeftTrialDays = (data: Subscription[]) => {
  if (!data) return null;
  const plan = R.find<Subscription>(
    R.pathEq(['subscription_plan', 'id'], TRIAL_SUBSCRIPTION_ID)
  )(data);
  if (!plan || !plan.active) {
    return null;
  }
  const now = dayjs.utc();
  const dueDate = dayjs.utc(R.prop('due_date', plan));
  const diffDays = dueDate.diff(now, 'days');

  if (diffDays === 0) {
    // could mean that it expires today, or expired today
    return now.isBefore(dueDate) ? 0 : -1;
  }
  return diffDays;
};

export const getIsAdminUser = (roles: Role[] = []) =>
  roles.some((role) => role.key_name === 'owner');

export const getIsBetaUser = (perms: string[]) =>
  perms.includes('subscriptions.can_use_beta_features') ||
  perms.includes('subscriptions.can_use_internal_beta_features');

export const getIsInternalBetaUser = createDeepEqualSelector(
  getActiveSubscriptionNames,
  (subs) => subs.includes('Internal Beta Access')
);

export const getIsShorePayUser = (perms: string[]) =>
  perms.includes('subscriptions.can_use_shore_pay') as boolean;

export const getCustomerDisplayUser = (perms: string[]) =>
  perms.includes('subscriptions.can_use_customer_display');

export const getGiftcardsV2User = (perms: string[]) =>
  perms.includes('subscriptions.can_use_giftcards_v2');

export const getIsTipsUser = (perms: string[]) =>
  perms.includes('subscriptions.can_use_tips');

export const getIsAPIKeysUser = (perms: string[]) =>
  perms.includes('subscriptions.can_use_api_keys');

export const getIsShopUser = (perms: string[]) =>
  perms.includes('subscriptions.can_have_shop');

export const getIsDatevUser = (perms: string[]) =>
  perms.includes('subscriptions.can_use_datev_service');

export const getCanSeeExtendedDashboard = (perms: string[]) =>
  perms.includes('subscriptions.can_see_extended_dashboard');

export const getCanSeeNewDashboardPlus = (perms: string[]) =>
  perms.includes('subscriptions.can_see_new_dashboard_plus');

export const getCanUseTwint = (perms: string[]) =>
  perms.includes('accounts.can_use_twint');

export const getCanUseDownpayment = (perms: string[]) =>
  perms.includes('subscriptions.can_use_downpayment');

export const getShowExtendedDashboard = (subs: Subscription[]) => {
  if (!subs || subs.length === 0) {
    return false;
  }

  const activeSubs = getActiveSubscriptions(subs);
  const activeSubNames = R.map(
    (it: Subscription) => it.subscription_plan.name,
    activeSubs
  );
  return activeSubNames.includes('can_see_new_dashboard_plus');
};
